import React, { useState, useEffect } from 'react';
import { deliveryRunAPI } from '../../services/api';
import './CreateRunModal.css';

/**
 * The "Create Delivery Run" dialog, shared by the Planning page and the
 * Delivery Diary.
 *
 * One dialog, so there is one way to create a run and one set of rules on
 * screen. The Planning page passes the day it is looking at; the Diary
 * passes today.
 *
 * The server owns the rules - which period a start time falls in, how many
 * vehicles are left in that period, and what code the run will be given -
 * and publishes all three on the check-availability payload. This dialog
 * shows what the server says. It never works the period out for itself,
 * because two copies of that rule drift apart the moment one of them
 * changes.
 */

/** The run's start time as "8:00 AM". */
const formatStartTime = (timeString) => {
  if (!timeString) return '';

  const match = String(timeString).match(/(\d{2}):(\d{2})/);
  if (!match) return timeString;

  const hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;

  return `${displayHours}:${minutes} ${period}`;
};

/**
 * @param {boolean}  isOpen        Whether the dialog is showing.
 * @param {Function} onClose       Called when the planner dismisses it.
 * @param {Function} onCreated     Called with the new run after it is saved.
 * @param {string}   defaultDate   The date the form starts on.
 */
const CreateRunModal = ({ isOpen, onClose, onCreated, defaultDate = '' }) => {
  const [newRunDate, setNewRunDate] = useState(defaultDate);
  const [newRunStartTime, setNewRunStartTime] = useState('');
  const [newRunName, setNewRunName] = useState('');
  const [formError, setFormError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [vehicleConcern, setVehicleConcern] = useState(null);
  const [runCodePreview, setRunCodePreview] = useState(null);

  // Opening the dialog starts from a clean form.
  useEffect(() => {
    if (!isOpen) return;

    setNewRunDate(defaultDate);
    setNewRunStartTime('');
    setNewRunName('');
    setFormError(null);
    setVehicleConcern(null);
    setRunCodePreview(null);
  }, [isOpen, defaultDate]);

  // Ask the server what the run will cost in vehicles and what code it will
  // get, every time the date or the time changes. The answer is shown
  // before the run is saved, so the planner sees the code they are about to
  // get rather than finding out afterwards.
  useEffect(() => {
    if (!isOpen || !newRunDate || !newRunStartTime) {
      setVehicleConcern(null);
      setRunCodePreview(null);
      return undefined;
    }

    // The planner can change the date or the time faster than the server
    // answers. A reply that arrives after the next question was asked is
    // about the wrong run, so it is dropped.
    let stale = false;

    (async () => {
      try {
        const result = await deliveryRunAPI.checkAvailability(newRunDate, newRunStartTime);
        if (stale) return;

        setRunCodePreview({
          code: result.code_preview,
          period: result.period,
          period_label: result.period_label,
          runs_in_period: result.runs_in_period,
        });

        if (!result.available || result.warning) {
          setVehicleConcern({
            message: result.message,
            available_vehicles: result.available_vehicles,
            existing_runs: result.existing_runs,
            existing_runs_count: result.existing_runs_count,
            // No vehicle at all is a hard stop. A reduced count is a warning
            // the planner is allowed to overrule.
            isError: !result.available && result.available_vehicles === 0,
          });
        } else {
          setVehicleConcern(null);
        }
      } catch (err) {
        if (stale) return;

        setVehicleConcern(null);
        setRunCodePreview(null);
      }
    })();

    return () => {
      stale = true;
    };
  }, [newRunDate, newRunStartTime, isOpen]);

  const handleCreateRun = async (e) => {
    e.preventDefault();
    setFormError(null);
    setCreating(true);

    try {
      const runData = {
        delivery_date: newRunDate,
        start_time: newRunStartTime,
        name: newRunName.trim() || null,
      };

      const response = await deliveryRunAPI.create(runData);

      if (onCreated) onCreated(response.run);
      onClose();
    } catch (err) {
      console.error('Error creating delivery run:', err);
      setFormError(err.response?.data?.message || 'Failed to create delivery run');
    } finally {
      setCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-run-modal-overlay" onClick={onClose}>
      <div className="create-run-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Delivery Run</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleCreateRun}>
          <div className="modal-body">
            {formError && (
              <div className="error-message">{formError}</div>
            )}

            <div className="form-group">
              <label htmlFor="new-run-date">Date</label>
              <input
                type="date"
                id="new-run-date"
                value={newRunDate}
                onChange={(e) => setNewRunDate(e.target.value)}
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="new-run-time">Start Time</label>
              <input
                type="time"
                id="new-run-time"
                value={newRunStartTime}
                onChange={(e) => setNewRunStartTime(e.target.value)}
                className="form-control"
                required
              />
              <div className="time-quick-select">
                <button
                  type="button"
                  className={`time-btn ${newRunStartTime === '08:00' ? 'selected' : ''}`}
                  onClick={() => setNewRunStartTime('08:00')}
                >
                  8:00 AM
                </button>
                <button
                  type="button"
                  className={`time-btn ${newRunStartTime === '13:00' ? 'selected' : ''}`}
                  onClick={() => setNewRunStartTime('13:00')}
                >
                  1:00 PM
                </button>
                <button
                  type="button"
                  className={`time-btn ${newRunStartTime === '15:00' ? 'selected' : ''}`}
                  onClick={() => setNewRunStartTime('15:00')}
                >
                  3:00 PM
                </button>
                <button
                  type="button"
                  className={`time-btn ${newRunStartTime === '16:00' ? 'selected' : ''}`}
                  onClick={() => setNewRunStartTime('16:00')}
                >
                  4:00 PM
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="new-run-name">Run Name (Optional)</label>
              <input
                type="text"
                id="new-run-name"
                value={newRunName}
                onChange={(e) => setNewRunName(e.target.value)}
                className="form-control"
                placeholder="e.g. Hamilton, Cambridge, Thames"
              />
              <span className="hint">
                The destination area for this run. The same name can be used
                many times over - one run a day to the same area is normal.
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="new-run-code">Code</label>
              <div className="form-control readonly" id="new-run-code">
                {runCodePreview?.code || '\u2014'}
              </div>
              <span className="hint">
                {runCodePreview
                  ? `Generated automatically. ${runCodePreview.period_label} period, ${runCodePreview.runs_in_period} run${runCodePreview.runs_in_period === 1 ? '' : 's'} already in this period.`
                  : 'Choose a date and a start time to see the code.'}
              </span>
            </div>

            {vehicleConcern && (
              <div className={`vehicle-concern ${vehicleConcern.isError ? 'error' : 'warning'}`}>
                <strong>{vehicleConcern.isError ? '⚠️ Error: ' : '⚠️ Warning: '}</strong>
                {vehicleConcern.message}
                <div className="vehicle-details">
                  <span>Available vehicles: {vehicleConcern.available_vehicles}</span>
                  <span>Existing runs: {vehicleConcern.existing_runs_count}</span>
                </div>
                {vehicleConcern.existing_runs && vehicleConcern.existing_runs.length > 0 && (
                  <div className="existing-runs-list">
                    <strong>Existing runs for this period:</strong>
                    <ul>
                      {vehicleConcern.existing_runs.map((run) => (
                        <li key={run.id}>{run.code || 'Unnamed'} - {formatStartTime(run.start_time)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={creating}
            >
              {creating ? 'Creating...' : 'Create Run'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRunModal;
